import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, PenSquare, Trash2, Check, X } from "lucide-react";

/**
 * SuperAdmin-only page to create named teams and drop many members in with checkboxes.
 * Team membership drives lead scope for managers (see backend `manager_team_user_ids`).
 */
export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [membersDlg, setMembersDlg] = useState(null); // { team, selected: Set }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([api.get("/teams"), api.get("/users")]);
      setTeams(t.data || []);
      setAllUsers(u.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { name: form.name.trim(), description: form.description };
    if (!payload.name) { toast.error("Team name is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/teams/${editing.id}`, payload);
        toast.success("Team updated");
      } else {
        await api.post("/teams", payload);
        toast.success("Team created");
      }
      setCreating(false); setEditing(null); setForm({ name: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Members will be detached.`)) return;
    try { await api.delete(`/teams/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openMembers = (team) => {
    const cur = new Set(team.members.map((m) => m.id));
    setMembersDlg({ team, selected: cur });
  };

  const toggleMember = (uid) => {
    setMembersDlg((prev) => {
      const s = new Set(prev.selected);
      if (s.has(uid)) s.delete(uid); else s.add(uid);
      return { ...prev, selected: s };
    });
  };

  const saveMembers = async () => {
    setBusy(true);
    try {
      await api.post(`/teams/${membersDlg.team.id}/members`, { user_ids: Array.from(membersDlg.selected) });
      toast.success(`${membersDlg.selected.size} member(s) saved`);
      setMembersDlg(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="teams-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Structure</p>
          <h1 className="text-xl font-semibold mt-1 text-slate-900">Teams</h1>
          <p className="text-slate-500 mt-1 text-sm">Group members into named teams. Managers see leads across every teammate in their team.</p>
        </div>
        <Button onClick={() => { setForm({ name: "", description: "" }); setEditing(null); setCreating(true); }} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="new-team-btn">
          <Plus className="w-4 h-4 mr-2" /> New Team
        </Button>
      </header>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : teams.length === 0 ? (
        <div className="solid-panel p-12 text-center">
          <Users className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">No teams yet. Create your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => (
            <div key={t.id} className="solid-panel p-4" data-testid={`team-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{t.name}</p>
                  {t.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</p>}
                </div>
                <div className="flex flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ name: t.name, description: t.description || "" }); setEditing(t); setCreating(true); }} data-testid={`edit-team-${t.id}`}>
                    <PenSquare className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-rose-600" data-testid={`del-team-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">Members · {t.member_count}</p>
              <div className="flex flex-wrap gap-1 mt-1.5 min-h-[24px]">
                {t.members.slice(0, 5).map((m) => (
                  <span key={m.id} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {m.name}{m.role === "manager" ? " ★" : ""}
                  </span>
                ))}
                {t.members.length > 5 && <span className="text-[11px] text-slate-500">+{t.members.length - 5} more</span>}
              </div>
              <Button size="sm" onClick={() => openMembers(t)} className="mt-3 w-full bg-slate-100 hover:bg-slate-200 text-slate-800" data-testid={`manage-members-${t.id}`}>
                <Users className="w-3.5 h-3.5 mr-1.5" /> Manage members
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md" data-testid="team-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Team name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus data-testid="team-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional — e.g. 'North zone enterprise sales'" data-testid="team-desc-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" disabled={busy} data-testid="save-team-btn">
                {busy ? "Saving…" : (editing ? "Save" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage members */}
      <Dialog open={!!membersDlg} onOpenChange={(v) => !v && setMembersDlg(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="members-dialog">
          <DialogHeader>
            <DialogTitle>Manage members — {membersDlg?.team?.name}</DialogTitle>
            <p className="text-xs text-slate-500">Tick everyone who belongs to this team. Managers on the team see leads for every teammate here.</p>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {allUsers.filter((u) => u.role !== "superadmin").map((u) => {
              const on = membersDlg?.selected.has(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleMember(u.id)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-md border mt-1.5 ${on ? "bg-indigo-50 border-indigo-200" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`member-toggle-${u.email}`}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center border ${on ? "bg-[#1D63ED] border-[#1D63ED] text-white" : "border-slate-300"}`}>
                    {on && <Check className="w-3 h-3" />}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">{u.name} {u.role === "manager" && <span className="text-[10px] text-indigo-600 ml-1">MGR</span>}</p>
                    <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMembersDlg(null)}>Cancel</Button>
            <Button onClick={saveMembers} disabled={busy} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="save-members-btn">
              {busy ? "Saving…" : `Save (${membersDlg?.selected.size || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
